<template>
	<div>
		<div class="t-page-header">
			<div>
				<h1>{{ mineOnly ? "המשימות שלי" : "כל המשימות" }}</h1>
				<div class="t-subtitle">תצוגת רשימה של {{ tasks.length }} משימות</div>
			</div>
			<button class="t-btn primary" @click="modal.open = true; modal.taskId = 0;">+ משימה חדשה</button>
		</div>

		<div class="t-toolbar">
			<input v-model="filters.q" placeholder="חיפוש..." @input="debouncedLoad" />
			<select v-model="filters.status" @change="load">
				<option value="">כל הסטטוסים</option>
				<option value="todo">להתחלה</option>
				<option value="in_progress">בעבודה</option>
				<option value="review">בבדיקה</option>
				<option value="done">הושלם</option>
				<option value="cancelled">בוטל</option>
			</select>
			<select v-if="!mineOnly" v-model="filters.assignee_id" @change="load">
				<option value="">כל האחראים</option>
				<option v-for="u in users" :key="u.id" :value="u.id">{{ u.name }}</option>
			</select>
		</div>

		<table v-if="tasks.length" class="t-table">
			<thead>
				<tr>
					<th style="width:48px;"></th>
					<th>משימה</th>
					<th>פרויקט</th>
					<th>אחראי</th>
					<th>סטטוס</th>
					<th>עדיפות</th>
					<th>דדליין</th>
					<th>תגובות</th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="t in tasks" :key="t.id" @click="open(t.id)">
					<td @click.stop>
						<button
							class="t-check"
							:class="{ done: t.status === 'done' }"
							:title="t.status === 'done' ? 'בטל סימון הושלם' : 'סמן כהושלם'"
							@click.stop="toggleDone(t)"
						>✓</button>
					</td>
					<td><b>{{ t.title }}</b></td>
					<td><span class="t-status-pill" :style="{background: t.project_color, color:'#fff'}">{{ t.project_name }}</span></td>
					<td>
						<span v-if="t.assignee_id" style="display:inline-flex; align-items:center; gap:6px;">
							<span class="t-avatar sm" :style="{background: t.assignee_color || '#6366f1'}">{{ initials(t.assignee_name) }}</span>
							{{ t.assignee_name }}
						</span>
						<span v-else style="color:var(--t-text-3);">—</span>
					</td>
					<td><span class="t-status-pill" :class="'s-' + t.status">{{ statusLabel(t.status) }}</span></td>
					<td>{{ priorityLabel(t.priority) }}</td>
					<td :class="{'t-deadline late': overdue(t)}" :style="overdue(t) ? 'color:var(--t-danger); font-weight:700' : ''">
						{{ t.deadline ? formatDate(t.deadline) : "—" }}
					</td>
					<td>{{ t.comments_count || 0 }}</td>
				</tr>
			</tbody>
		</table>

		<div v-else class="t-empty">
			<div class="t-empty-icon">📭</div>
			לא נמצאו משימות
		</div>

		<TaskModal v-if="modal.open" :task-id="modal.taskId" :user="user" :users="users" :projects-list="projectsList" @close="modal.open = false" @saved="load" />
	</div>
</template>

<script>
import { TasksAPI, TasksRealtime, initials, formatDate, isOverdue } from "./TasksApi";
import TaskModal from "./TaskModal.vue";

export default {
	name: "TasksAll",
	components: { TaskModal },
	props: {
		user: Object,
		users: Array,
		mineOnly: { type: Boolean, default: false },
		openTaskId: { type: Number, default: 0 },
		projectsList: { type: Array, default: () => [] },
	},
	emits: ["task-opened"],
	data() {
		return {
			tasks: [],
			filters: { q: "", status: "", assignee_id: "" },
			modal: { open: false, taskId: 0 },
			subscription: null,
			debouncedLoad: () => {},
		};
	},
	async mounted() {
		// Default the assignee filter to the logged-in user on the "all tasks" view.
		// The dropdown stays editable so the user can switch to "all assignees".
		if (!this.mineOnly && this.user && this.user.id && this.filters.assignee_id === "") {
			this.filters.assignee_id = this.user.id;
		}
		this.debouncedLoad = this.debounce(this.load, 350);
		// In-place updates instead of reload; if a new task lands we schedule a single reload.
		const lazyReload = (() => { let h; return () => { clearTimeout(h); h = setTimeout(() => this.load(), 700); }; })();
		const sub = TasksRealtime.subscribe("tasks-global", {
			"task.created": lazyReload,
			"task.updated": lazyReload,
		});
		const [, subRes] = await Promise.all([this.load(), sub]);
		this.subscription = subRes;
		if (this.openTaskId) {
			this.modal.taskId = this.openTaskId;
			this.modal.open = true;
			this.$emit("task-opened");
		}
	},
	beforeUnmount() { if (this.subscription) TasksRealtime.unsubscribe(this.subscription); },
	watch: {
		mineOnly() { this.load(); },
		openTaskId(v) {
			if (v) {
				this.modal.taskId = v;
				this.modal.open = true;
				this.$emit("task-opened");
			}
		},
	},
	methods: {
		initials, formatDate,
		debounce(fn, t = 250) { let h; return (...a) => { clearTimeout(h); h = setTimeout(() => fn(...a), t); }; },
		overdue(t) { return isOverdue(t.deadline, t.status); },
		statusLabel(s) { return { todo: "להתחלה", in_progress: "בעבודה", review: "בבדיקה", done: "הושלם", cancelled: "בוטל" }[s] || s; },
		priorityLabel(p) { return { low: "נמוכה", medium: "בינונית", high: "גבוהה", urgent: "דחוף" }[p] || p; },
		async load() {
			const f = { ...this.filters };
			Object.keys(f).forEach(k => { if (f[k] === "" || f[k] === null) delete f[k]; });
			if (this.mineOnly) f.mine = 1;
			const res = await TasksAPI.tasksList(f);
			this.tasks = (res && res.items) || [];
		},
		open(id) { this.modal.taskId = id; this.modal.open = true; },
		async toggleDone(t) {
			const next = t.status === "done" ? "todo" : "done";
			const prev = t.status;
			t.status = next; // optimistic
			const res = await TasksAPI.taskStatus(t.id, next);
			if (res && res.error) t.status = prev; // revert on failure
		},
	},
};
</script>
